import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Edit, UserPlus, ShieldPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formatearFecha = (fecha) => {
	if (!fecha) return "...";
	try {
		return new Date(fecha).toLocaleDateString("es-ES");
	} catch (e) {
		return fecha; // Si no es una fecha válida, mostrarla como está
	}
};

function Usuarios() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [usuarios, setUsuarios] = useState([]);
	const [habitaciones, setHabitaciones] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modalType, setModalType] = useState(null); // 'inquilino', 'gestor', 'editar'
	const [editingUsuario, setEditingUsuario] = useState(null);

	const [formData, setFormData] = useState({
		email: "",
		password: "",
		nombre: "",
		apellidos: "",
		dni: "",
		tipo_usuario: "inquilino",
		habitacion_id: "",
		fecha_inicio: "",
		fecha_fin: "",
	});

	const isActivo = useCallback((usuario) => {
		if (usuario.tipo_usuario === "inquilino") {
			return (
				usuario.fecha_fin === null ||
				usuario.fecha_fin > new Date().toISOString().split("T")[0]
			);
		}
		return true; // Gestores siempre están activos
	}, []);

	const cargarDatos = useCallback(async () => {
		try {
			setLoading(true);

			const [usuariosResponse, habitacionesResponse] = await Promise.all([
				axios.get("/api/users"),
				axios.get("/api/habitaciones"),
			]);

			const sortedUsuarios = usuariosResponse.data.sort((a, b) => {
				const aIsActivo = isActivo(a);
				const bIsActivo = isActivo(b);
				if (aIsActivo !== bIsActivo) {
					return aIsActivo ? -1 : 1;
				}
				return a.nombre.localeCompare(b.nombre);
			});

			setUsuarios(sortedUsuarios);
			setHabitaciones(habitacionesResponse.data);
		} catch (error) {
			console.error("Error al cargar datos:", error);
		} finally {
			setLoading(false);
		}
	}, [isActivo]);

	useEffect(() => {
		if (user?.tipo_usuario === "gestor") {
			cargarDatos();
		}
	}, [user, cargarDatos]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			if (editingUsuario) {
				// Lógica de actualización
				const updateData = { ...formData };
				if (!updateData.password) delete updateData.password;

				// Convertir vacíos a null para campos opcionales
				if (updateData.habitacion_id === "") updateData.habitacion_id = null;
				if (updateData.fecha_inicio === "") updateData.fecha_inicio = null;
				if (updateData.fecha_fin === "") updateData.fecha_fin = null;

				await axios.put(`/api/users/${editingUsuario.id}`, updateData);
			} else if (modalType === "inquilino") {
				// Crear nuevo inquilino
				const {
					nombre,
					apellidos,
					dni,
					habitacion_id,
					fecha_inicio,
					fecha_fin,
				} = formData;
				await axios.post("/api/users", {
					nombre,
					apellidos,
					dni,
					habitacion_id: habitacion_id || null,
					fecha_inicio: fecha_inicio || null,
					fecha_fin: fecha_fin || null,
				});
			} else if (modalType === "gestor") {
				// Crear nuevo gestor
				const { nombre, email, password } = formData;
				await axios.post("/api/auth/register-gestor", {
					nombre,
					email,
					password,
				});
			}

			closeModal();
			cargarDatos();
		} catch (error) {
			console.error("Error al guardar usuario:", error);
			alert(error.response?.data?.message || "Error al guardar el usuario");
		}
	};

	// const eliminarUsuario = async (usuarioId) => {
	// 	const confirmacion = window.confirm(
	// 		"¿Estás seguro de que quieres eliminar este usuario?"
	// 	);
	//
	// 	if (confirmacion) {
	// 		try {
	// 			await axios.delete(`/api/users/${usuarioId}`);
	// 			cargarDatos();
	// 		} catch (error) {
	// 			console.error("Error al eliminar usuario:", error);
	// 			alert(error.response?.data?.message || "Error al eliminar el usuario");
	// 		}
	// 	}
	// };

	const openEditModal = (usuario) => {
		setEditingUsuario(usuario);
		setModalType("editar");
		setFormData({
			nombre: usuario.nombre || "",
			apellidos: usuario.apellidos || "",
			dni: usuario.dni || "",
			email: usuario.email || "",
			password: "", // No mostramos la password actual
			tipo_usuario: usuario.tipo_usuario,
			habitacion_id: usuario.habitacion_id || "",
			fecha_inicio: usuario.fecha_inicio
				? new Date(usuario.fecha_inicio).toISOString().split("T")[0]
				: "",
			fecha_fin: usuario.fecha_fin
				? new Date(usuario.fecha_fin).toISOString().split("T")[0]
				: "",
		});
	};

	const openCreateInquilinoModal = () => {
		setEditingUsuario(null);
		setModalType("inquilino");
		setFormData({
			nombre: "",
			apellidos: "",
			dni: "",
			email: "",
			password: "",
			tipo_usuario: "inquilino",
			habitacion_id: "",
			fecha_inicio: "",
			fecha_fin: "",
		});
	};

	const openCreateGestorModal = () => {
		setEditingUsuario(null);
		setModalType("gestor");
		setFormData({
			nombre: "",
			apellidos: "",
			dni: "",
			email: "",
			password: "",
			tipo_usuario: "gestor",
			habitacion_id: "",
			fecha_inicio: "",
			fecha_fin: "",
		});
	};

	const closeModal = () => {
		setModalType(null);
		setEditingUsuario(null);
	};

	// Verificar permisos
	if (user?.tipo_usuario !== "gestor") {
		return (
			<div className="container">
				<div className="access-denied">
					<h1>Acceso Denegado</h1>
					<p>No tienes permisos para acceder a esta página.</p>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="loading">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div className="container">
			<div className="page-header">
				<div>
					<h1>Gestión de Usuarios</h1>
					<p>Crea, edita y administra las cuentas de gestores e inquilinos.</p>
				</div>

				<div className="header-actions">
					<button
						onClick={openCreateInquilinoModal}
						className="btn btn-primary"
					>
						<UserPlus size={16} />
						Nuevo Inquilino
					</button>
					<button onClick={openCreateGestorModal} className="btn btn-secondary">
						<ShieldPlus size={16} />
						Nuevo Gestor
					</button>
				</div>
			</div>

			<div className="usuarios-grid">
				{usuarios.map((usuario) => (
					<div key={usuario.id} className="usuario-card">
						<div className="usuario-header">
							<div
								className={`usuario-avatar ${
									isActivo(usuario) ? "avatar-activo" : "avatar-inactivo"
								}`}
							>
								{usuario.nombre.charAt(0)}
							</div>
							<div className="usuario-info">
								<h3>
									{usuario.nombre} {usuario.apellidos || ""}
								</h3>
								<p>{usuario.dni || usuario.email}</p>
							</div>
							<button
								onClick={() => openEditModal(usuario)}
								className="btn-icon"
								title="Editar usuario"
							>
								<Edit size={16} />
							</button>
						</div>
						<div className="usuario-body">
							<div className="usuario-detail">
								<span className="detail-label">Rol:</span>
								<span className="detail-value">
									{usuario.tipo_usuario === "gestor" ? "Gestor" : "Inquilino"}
								</span>
							</div>
							{usuario.tipo_usuario === "inquilino" && (
								<>
									<div className="usuario-detail">
										<span className="detail-label">Habitación:</span>
										<span className="detail-value">
											{usuario.habitacion_numero || "No asignada"}
										</span>
									</div>
									<div className="usuario-detail">
										<span className="detail-label">Estancia:</span>
										<span className="detail-value">
											{formatearFecha(usuario.fecha_inicio)} -{" "}
											{formatearFecha(usuario.fecha_fin) || "Presente"}
										</span>
									</div>
								</>
							)}
						</div>
						<div className="usuario-footer">
							<span
								className={`badge ${
									isActivo(usuario) ? "badge-success" : "badge-secondary"
								}`}
							>
								{isActivo(usuario) ? "Activo" : "Baja"}
							</span>
							{usuario.tipo_usuario === "inquilino" && (
								<button
									className="btn btn-sm btn-outline"
									style={{ marginLeft: 8, marginTop: 4 }}
									onClick={() => navigate(`/resumen-usuario/${usuario.id}`)}
									title="Ver resumen de pagos y deudas"
								>
									Ver resumen
								</button>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Modal crear/editar usuario */}
			{modalType && (
				<div className="modal-overlay" onClick={closeModal}>
					<div className="modal" onClick={(e) => e.stopPropagation()}>
						<h2>
							{editingUsuario
								? "Editar Usuario"
								: modalType === "inquilino"
								? "Nuevo Inquilino"
								: "Nuevo Gestor"}
						</h2>

						<form onSubmit={handleSubmit}>
							{/* Campos comunes */}
							<div className="form-group">
								<label className="form-label">Nombre *</label>
								<input
									type="text"
									value={formData.nombre}
									onChange={(e) =>
										setFormData({
											...formData,
											nombre: e.target.value,
										})
									}
									className="form-input"
									required
								/>
							</div>

							{/* Campos solo para Inquilino o editando Inquilino */}
							{(modalType === "inquilino" ||
								(editingUsuario &&
									editingUsuario.tipo_usuario === "inquilino")) && (
								<>
									<div className="form-group">
										<label className="form-label">Apellidos *</label>
										<input
											type="text"
											value={formData.apellidos}
											onChange={(e) =>
												setFormData({
													...formData,
													apellidos: e.target.value,
												})
											}
											className="form-input"
											required
										/>
									</div>
									<div className="form-group">
										<label className="form-label">DNI *</label>
										<input
											type="text"
											value={formData.dni}
											onChange={(e) =>
												setFormData({
													...formData,
													dni: e.target.value,
												})
											}
											className="form-input"
											required
										/>
									</div>
								</>
							)}

							{/* Campos solo para Gestor o editando Gestor */}
							{(modalType === "gestor" ||
								(editingUsuario &&
									editingUsuario.tipo_usuario === "gestor")) && (
								<>
									<div className="form-group">
										<label className="form-label">Email *</label>
										<input
											type="email"
											value={formData.email}
											onChange={(e) =>
												setFormData({
													...formData,
													email: e.target.value,
												})
											}
											className="form-input"
											required
										/>
									</div>
									<div className="form-group">
										<label className="form-label">
											Contraseña{" "}
											{editingUsuario ? "(dejar vacío para no cambiar)" : "*"}
										</label>
										<input
											type="password"
											value={formData.password}
											onChange={(e) =>
												setFormData({
													...formData,
													password: e.target.value,
												})
											}
											className="form-input"
											required={!editingUsuario}
											minLength="6"
										/>
									</div>
								</>
							)}

							{/* Campos de asignación (solo para Inquilinos o al editar) */}
							{(modalType === "inquilino" || editingUsuario) && (
								<>
									<div className="form-group">
										<label className="form-label">Habitación</label>
										<select
											value={formData.habitacion_id}
											onChange={(e) =>
												setFormData({
													...formData,
													habitacion_id: e.target.value,
												})
											}
											className="form-select"
										>
											<option value="">Sin asignar</option>
											{habitaciones.map((habitacion) => (
												<option key={habitacion.id} value={habitacion.id}>
													{habitacion.numero}
												</option>
											))}
										</select>
									</div>
									<div className="grid grid-cols-2">
										<div className="form-group">
											<label className="form-label">Fecha de Inicio</label>
											<input
												type="date"
												value={formData.fecha_inicio}
												onChange={(e) =>
													setFormData({
														...formData,
														fecha_inicio: e.target.value,
													})
												}
												className="form-input"
											/>
										</div>
										<div className="form-group">
											<label className="form-label">Fecha de Fin</label>
											<input
												type="date"
												value={formData.fecha_fin}
												onChange={(e) =>
													setFormData({
														...formData,
														fecha_fin: e.target.value,
													})
												}
												className="form-input"
											/>
										</div>
									</div>
								</>
							)}

							<div className="modal-actions">
								<button
									type="button"
									onClick={closeModal}
									className="btn btn-outline"
								>
									Cancelar
								</button>
								<button type="submit" className="btn btn-primary">
									{editingUsuario ? "Actualizar" : "Crear"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default Usuarios;
